import { useMelwaysInfo } from '@seek/melways-react';
import {
  HalfwayTriggerOnce,
  type Market,
  brandNameMapper,
} from '@ts-internal/shared-lib';
import { Head } from '@unhead/react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Stack,
  Columns,
  Column,
  Hidden,
  ContentBlock,
  Text,
} from 'braid-design-system';
import { useEffect, useState, useMemo, useRef } from 'react';

import { useFilters } from 'src/App/components/Filters/hooks/useFilters';
import { LoginPromptBanner } from 'src/App/components/LoginPrompt';
import { LoginPromptModal } from 'src/App/components/LoginPrompt/LoginPromptModal';
import {
  NudgeProfilePublicBanner,
  NudgeProfileUpdateBanner,
} from 'src/App/components/NudgeBanner';
import { SearchBanner } from 'src/App/components/SearchBanner/SearchBanner';
import { SearchLandingPage } from 'src/App/components/SearchLandingPage/SearchLandingPage';
import { SearchResult } from 'src/App/components/SearchResult/SearchResult';
import { useAppConfig } from 'src/App/providers/AppConfigProvider';
import {
  defaultPageNumber,
  defaultPageSize,
} from 'src/App/services/search/searchDefaults';
import { useSearch } from 'src/App/services/search/useSearch';
import { useSearchQueryParams } from 'src/App/services/search/useSearchQueryParams';
import { useHubbleClient } from 'src/App/services/tracking/useHubbleClient';
import { useLoginPromptTracking } from 'src/App/services/tracking/useLoginPromptTracking';
import { useAuthLinksWithReturnUrl } from 'src/App/services/urls/useAuthLinksWithReturnUrl';
import { useGetCandidateDetailsQuery } from 'src/graphql/Queries/getCandidateDetails.generated';

import { Filters } from '../../components/Filters/Filters';

import translations from './.vocab';

import { resetPagePaddingBottomStyle } from './Search.css';

export const Search = () => {
  const { t } = useTranslations(translations);

  const { isAuthenticated } = useAppConfig();
  const { brand } = useMelwaysInfo();

  const {
    trackProfileSearchDisplayedEvent,
    trackProfileSearchResultsDisplayedEvent,
  } = useHubbleClient();

  const {
    searchQueryFromUrl,
    marketFromUrl,
    pageNumberFromUrl,
    roleTitlesFromUrl,
    matchLatestRoleTitleFromUrl,
    locationsFromUrl,
    companiesFromUrl,
    matchLatestCompanyFromUrl,
  } = useSearchQueryParams();

  const { loginUrl, googleLoginUrl, registerUrl } = useAuthLinksWithReturnUrl();

  const { data: candidateDetails, loading: candidateDetailsLoading } =
    useGetCandidateDetailsQuery({
      skip: !isAuthenticated,
    });

  const [isValidSearchQuery, setIsValidSearchQuery] = useState<boolean>(true);
  const [marketState, setMarket] = useState<Market>(marketFromUrl);
  const [pageNumberState, setPageNumber] = useState<number>(pageNumberFromUrl);
  const [showfilterLoginModalState, setShowfilterLoginModal] = useState(false);
  const [loginModalTrigger, setLoginModalTrigger] = useState<
    'filter' | 'halfway' | null
  >(null);

  // whenever pagination changes, scroll to the top of the page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pageNumberState]);

  const {
    searchQueryState,
    searchResults,
    csrError,
    csrLoading,
    csrSearch,
    setSearchQuery,
  } = useSearch();
  const {
    roleTitle,
    location,
    company,
    mobilePanelState,
    filterItemsCount,
    reset: resetFilters,
  } = useFilters({
    intialValues: isAuthenticated
      ? {
          roleTitle: roleTitlesFromUrl,
          matchLatestRoleTitle: matchLatestRoleTitleFromUrl,
          location: locationsFromUrl,
          company: companiesFromUrl,
          matchLatestCompany: matchLatestCompanyFromUrl,
          // add more filters here
        }
      : undefined,
  });

  const totalCount = searchResults?.pageInfo?.totalMatches ?? 0;

  const serviceToken = searchResults?.serviceToken;

  const shouldTriggerHalfwayLogin =
    !isAuthenticated && !csrLoading && totalCount > defaultPageSize;

  const filterModalTrackingHook = useLoginPromptTracking(
    'people search',
    'search filters',
    'popup',
  );

  const halfwayModalTrackingHook = useLoginPromptTracking(
    'people search',
    'search results mid',
    'popup',
  );

  const bannerTrackingHook = useLoginPromptTracking(
    'people search',
    'search results',
  );

  const modalTrackingCallbacks = useMemo(() => {
    const trackingHook =
      loginModalTrigger === 'filter'
        ? filterModalTrackingHook
        : halfwayModalTrackingHook;

    return {
      onComponentDisplayed: trackingHook.trackComponentDisplayed,
      onLoginPressed: trackingHook.trackLoginPressed,
      onAuthLinkPressed: trackingHook.trackAuthLinkPressed,
    };
  }, [loginModalTrigger, filterModalTrackingHook, halfwayModalTrackingHook]);

  const bannerTrackingCallbacks = useMemo(
    () => ({
      onComponentDisplayed: bannerTrackingHook.trackComponentDisplayed,
      onLoginPressed: bannerTrackingHook.trackLoginPressed,
      onAuthLinkPressed: bannerTrackingHook.trackAuthLinkPressed,
    }),
    [
      bannerTrackingHook.trackComponentDisplayed,
      bannerTrackingHook.trackLoginPressed,
      bannerTrackingHook.trackAuthLinkPressed,
    ],
  );

  const shouldShowNotManyResultBanner =
    isAuthenticated &&
    !candidateDetailsLoading &&
    !csrLoading &&
    totalCount <= 20 &&
    candidateDetails?.viewer?.profileVisibility2?.id !== 'Public';

  const shouldShowKeepProfileFreshBanner =
    isAuthenticated &&
    !candidateDetailsLoading &&
    !csrLoading &&
    totalCount <= 20 &&
    candidateDetails?.viewer?.profileVisibility2?.id === 'Public';

  const hasTrackedProfileSearch = useRef(false);

  useEffect(() => {
    setSearchQuery(searchQueryFromUrl);
    setMarket(marketFromUrl);
    setPageNumber(pageNumberFromUrl);
  }, [marketFromUrl, pageNumberFromUrl, searchQueryFromUrl, setSearchQuery]);

  useEffect(() => {
    if (searchQueryFromUrl || hasTrackedProfileSearch.current) {
      return;
    }

    trackProfileSearchDisplayedEvent({
      currentPage: 'people search',
    });
    hasTrackedProfileSearch.current = true;
  }, [searchQueryFromUrl, trackProfileSearchDisplayedEvent]);

  useEffect(() => {
    if (!serviceToken || csrLoading) {
      return;
    }

    trackProfileSearchResultsDisplayedEvent({
      currentPage: 'people search',
      serviceToken,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceToken]);

  const renderEmptyState = () => (
    <SearchLandingPage
      isAuthenticated={isAuthenticated}
      candidateDetails={candidateDetails}
      candidateDetailsLoading={candidateDetailsLoading}
      domainMarket={marketFromUrl}
    />
  );

  const renderSearchResults = () => {
    if (csrError) {
      return (
        <Box background="body" borderRadius="large" padding="xxxlarge">
          <Box
            maxWidth="xsmall"
            textAlign="center"
            style={{ margin: '0 auto' }}
          >
            <Stack space="medium">
              <Text weight="medium">{t('WE_ARE_WORING_ON_IT')}</Text>
              <Text tone="secondary">
                {t('WE_CANNOT_SHOW_THESE_PROFILES_RIGHT_NOW')}
              </Text>
            </Stack>
          </Box>
        </Box>
      );
    }

    return (
      <SearchResult
        isLoading={csrLoading}
        profileSearchResults={searchResults}
        pageNumber={pageNumberState}
        isAuthenticated={isAuthenticated}
        authUrls={{
          loginUrl,
          registerUrl,
          googleLoginUrl,
        }}
        handlePageChange={(nextPage) => {
          csrSearch(
            {
              searchQuery: searchQueryState,
              market: marketState,
              pageNumber: nextPage,
              roleTitles: roleTitle.selectedItemRawStrings,
              locations: location.selectedItemRawStrings,
              matchLatestRoleTitle: roleTitle.matchLatestRoleTitle,
              companies: company.selectedItemRawStrings,
              matchLatestCompany: company.matchLatestCompany,
            },
            {
              useLastCommittedQuery: true,
            },
          );
        }}
        market={marketState}
      />
    );
  };

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow, noimageindex" />
        <title>
          {t('PEOPLE_SEARCH_BRAND', { brand: brandNameMapper[brand] })}
        </title>
        <meta
          name="description"
          content={t('SEARCH_FOR_RIGHT_PEOPLE_ON_BRAND', {
            brand: brandNameMapper[brand],
          })}
        />
      </Head>
      <Box
        background={searchQueryFromUrl ? 'neutralSoft' : 'body'}
        className={resetPagePaddingBottomStyle}
      >
        <Box overflow="hidden">
          <SearchBanner
            searchQuery={{
              value: searchQueryState,
              valid: isValidSearchQuery,
              errorMessage: !isValidSearchQuery
                ? t('PLEASE_ENTER_A_NAME')
                : undefined,
              onChange: (e) =>
                setSearchQuery((e.target as HTMLInputElement).value),
              onClear: () => setSearchQuery(''),
            }}
            market={{
              value: marketState,
              onChange: (marketSelected) => setMarket(marketSelected),
            }}
            domainMarket={marketFromUrl}
            filterItemsCount={filterItemsCount}
            showFilterButton={Boolean(searchQueryFromUrl)}
            onSubmit={(e) => {
              e.preventDefault();

              if (searchQueryState.trim().length === 0) {
                setIsValidSearchQuery(false);
                return;
              }

              setIsValidSearchQuery(true);

              csrSearch(
                {
                  searchQuery: searchQueryState,
                  market: marketState,
                  pageNumber: defaultPageNumber,
                  roleTitles: roleTitle.selectedItemRawStrings,
                  locations: location.selectedItemRawStrings,
                  matchLatestRoleTitle: roleTitle.matchLatestRoleTitle,
                  companies: company.selectedItemRawStrings,
                  matchLatestCompany: company.matchLatestCompany,
                },
                {
                  useLastCommittedQuery: false,
                },
              );
            }}
            onFiltersButtonClick={() => {
              if (!isAuthenticated) {
                setLoginModalTrigger('filter');
                setShowfilterLoginModal(true);
                return;
              }

              mobilePanelState.setIsOpen(true);
            }}
          />
        </Box>
        <Box paddingX={{ mobile: 'none', tablet: 'small' }} paddingY="small">
          <ContentBlock width="large">
            <LoginPromptModal
              open={showfilterLoginModalState}
              loginUrl={loginUrl}
              googleLoginUrl={googleLoginUrl}
              registerUrl={registerUrl}
              onClose={() => {
                setShowfilterLoginModal(false);
                setLoginModalTrigger(null);
              }}
              trackingCallbacks={modalTrackingCallbacks}
            />
            <Box>
              {!searchQueryFromUrl ? (
                renderEmptyState()
              ) : (
                <Columns space="small">
                  <Hidden below="tablet">
                    <Column width="content">
                      <Filters
                        disabled={!isAuthenticated}
                        filterItemsCount={filterItemsCount}
                        roleTitleState={roleTitle}
                        locationState={location}
                        companyState={company}
                        mobilePanelState={mobilePanelState}
                        onClick={() => {
                          if (!isAuthenticated) {
                            setLoginModalTrigger('filter');
                            setShowfilterLoginModal(true);
                            return;
                          }
                        }}
                        onFilterChanged={({
                          roleTitleSelectedItems,
                          locationSelectedItems,
                          matchLatestRoleTitle,
                          companySelectedItems,
                          matchLatestCompany,
                        }) => {
                          csrSearch(
                            {
                              searchQuery: searchQueryState,
                              market: marketState,
                              pageNumber: defaultPageNumber,
                              roleTitles: roleTitleSelectedItems.map(
                                (item) => item.name,
                              ),
                              locations: locationSelectedItems.map(
                                (item) => `${item.name} ${item.id}`,
                              ),
                              companies: companySelectedItems.map(
                                (item) => item.name,
                              ),
                              matchLatestRoleTitle,
                              matchLatestCompany,
                            },
                            {
                              useLastCommittedQuery: true,
                            },
                          );
                        }}
                        onClearAll={resetFilters}
                        marketState={{
                          value: marketState,
                          onChange: (marketSelected) =>
                            setMarket(marketSelected),
                        }}
                      />
                    </Column>
                  </Hidden>

                  <Column>
                    <Stack space="small">
                      <Box position="relative">
                        <HalfwayTriggerOnce
                          enabled={shouldTriggerHalfwayLogin}
                          action={() => {
                            // Workaround to blur the current focused element
                            // in order for Dialog not to auto scroll to the last focused element unexpectedly.
                            (document.activeElement as HTMLElement)?.blur();

                            setLoginModalTrigger('halfway');
                            setShowfilterLoginModal(true);
                          }}
                        />
                        {renderSearchResults()}
                      </Box>
                      {!isAuthenticated && totalCount <= defaultPageSize && (
                        <LoginPromptBanner
                          loginUrl={loginUrl}
                          googleLoginUrl={googleLoginUrl}
                          registerUrl={registerUrl}
                          trackingCallbacks={bannerTrackingCallbacks}
                        />
                      )}

                      {shouldShowNotManyResultBanner && (
                        <NudgeProfilePublicBanner />
                      )}

                      {shouldShowKeepProfileFreshBanner && (
                        <NudgeProfileUpdateBanner />
                      )}
                    </Stack>
                  </Column>
                </Columns>
              )}
            </Box>
          </ContentBlock>
        </Box>
      </Box>
    </>
  );
};
